import {
  NEURAL_TRANS_EDGE_FADE_LERP,
  NEURAL_TRANS_FADE_LERP,
  NEURAL_LAYOUT_JUMP_LERP,
  NEURAL_TRANS_POS_LERP,
} from '../../constants';
import { createClusterNebula, createGlowTexture, createHoverRing, createSelectionRing } from '../neuralMaterials';
import { createEdgeController } from './edges';
import { createHullController } from './hulls';
import { createNodeInstanceLayer } from '../nodeInstances';
import { NODE_DISK } from './constants';
import { createNodeController } from './nodes';
import { createGraphSync } from './sync';

/**
 * Graph meshes, labels, edges, halos, and sync helpers for the neural scene.
 */
export function createNeuralGraphLayer({ nodeGroup, graphRef, renameTagRef }) {
  const glowTexture = createGlowTexture();
  const nebula = createClusterNebula(renameTagRef);
  nebula.visible = false;
  nodeGroup.add(nebula);

  /** @type {Map<string, THREE.Mesh>} */
  const meshByTaskId = new Map();
  /** @type {Map<string, { halo: THREE.Mesh, mesh: THREE.Mesh, pulse: boolean }>} */
  const haloByTaskId = new Map();
  /** @type {Map<string, { label: CSS2DObject, element: HTMLElement }>} */
  const labelByTaskId = new Map();
  /** @type {THREE.Mesh[]} meshes for occlusion sampling (non-exiting visible only) */
  const raycastTargets = [];
  /** @type {Map<string, THREE.Line>} */
  const edgeLinesByKey = new Map();
  const lineResolution = { w: 1, h: 1 };
  /** @type {Map<string, THREE.Mesh>} soft light pockets under each nest, keyed by hub task id */
  const hullByHubId = new Map();

  const nodeInstances = createNodeInstanceLayer(nodeGroup);

  const selectionRing = createSelectionRing();
  nodeGroup.add(selectionRing);
  const hoverRing = createHoverRing();
  nodeGroup.add(hoverRing);

  const {
    createMeshesForNode,
    reconcileNodePresentation,
    rebuildRaycastTargets,
    placeLabelNearMesh,
    mountLabel,
    removeHalo,
    detachLabelForTask,
    disposeMissionMesh,
  } = createNodeController({
    nodeGroup,
    meshByTaskId,
    haloByTaskId,
    labelByTaskId,
    raycastTargets,
  });

  const {
    reconcileEdges,
    refreshOccluders,
    markOccludersDirty,
    finishOccluderClip,
    updateEdgeCurveGeometry,
    setLineResolution,
  } = createEdgeController({
    nodeGroup,
    meshByTaskId,
    edgeLinesByKey,
    lineResolution,
  });

  const { reconcileHulls, updateHulls } = createHullController({ nodeGroup, hullByHubId });

  const {
    ensureGraphSynced,
    refreshPresentation,
    syncSelection,
    getSelectedTaskId,
  } = createGraphSync({
    graphRef,
    meshByTaskId,
    labelByTaskId,
    edgeLinesByKey,
    nebula,
    selectionRing,
    createMeshesForNode,
    reconcileNodePresentation,
    reconcileEdges,
    reconcileHulls,
    rebuildRaycastTargets,
  });

  return {
    nodeGroup,
    meshByTaskId,
    haloByTaskId,
    labelByTaskId,
    raycastTargets,
    edgeLinesByKey,
    selectionRing,
    hoverRing,
    ensureGraphSynced,
    refreshPresentation,
    syncSelection,
    rebuildRaycastTargets,
    placeLabelNearMesh,
    mountLabel,
    refreshOccluders,
    markOccludersDirty,
    finishOccluderClip,
    updateEdgeCurveGeometry,
    removeHalo,
    detachLabelForTask,
    disposeMissionMesh,
    getSelectedTaskId,
    glowTexture,
    nebula,
    updateHulls,
    hullByHubId,
    setLineResolution,
    syncNodeInstances(camera, pulseT) {
      nodeInstances.sync(meshByTaskId, haloByTaskId, camera, pulseT);
    },
    disposeNodeInstances() {
      nodeInstances.dispose();
      meshByTaskId.forEach((mesh) => {
        if (mesh.geometry && mesh.geometry !== NODE_DISK) mesh.geometry.dispose?.();
        mesh.material?.dispose?.();
      });
      haloByTaskId.forEach(({ halo }) => {
        if (!halo.userData.sharedGeom) halo.geometry?.dispose?.();
        halo.material?.dispose?.();
      });
    },
    constants: {
      NEURAL_TRANS_POS_LERP,
      NEURAL_LAYOUT_JUMP_LERP,
      NEURAL_TRANS_FADE_LERP,
      NEURAL_TRANS_EDGE_FADE_LERP,
    },
  };
}
